import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import EducatorLayout from './components/EducatorLayout';
import supportResourcesApi from '../api/supportResources';
import { listSubjects } from '../api/subjects';

const EducatorResources = () => {
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tagFilter, setTagFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    audience: 'student',
    subjectId: '',
    grade: '',
    tags: '',
    files: [],
  });

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (tagFilter) params.tags = tagFilter;
      if (subjectFilter) params.subjectId = subjectFilter;
      if (gradeFilter) params.grade = gradeFilter;
      const data = await supportResourcesApi.list(params);
 setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);
        const [resourceData, subjectData] = await Promise.all([
          supportResourcesApi.list(),
          listSubjects(),
        ]);
 setItems(Array.isArray(resourceData) ? resourceData : []);
 setSubjects(Array.isArray(subjectData) ? subjectData : []);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load resources');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  useEffect(() => {
    load();
  }, [tagFilter, subjectFilter, gradeFilter]);

  const gradeOptions = useMemo(() => {
    const values = subjects.map((s) => s.grade).filter((g) => g != null);
    return Array.from(new Set(values)).sort((a, b) => a - b);
  }, [subjects]);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'subjectId') {
      const subject = subjects.find((s) => s._id === value);
      if (subject?.grade) {
        setForm((prev) => ({ ...prev, grade: subject.grade }));
      }
    }
  };

  const handleFiles = (evt) => {
    const files = Array.from(evt.target.files || []);
    setForm((prev) => ({ ...prev, files }));
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      if (form.title) formData.append('title', form.title);
      if (form.description) formData.append('description', form.description);
      if (form.audience) formData.append('audience', form.audience);
      if (form.subjectId) formData.append('subjectId', form.subjectId);
      if (form.grade) formData.append('grade', form.grade);
      const tags = form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      tags.forEach((tag) => formData.append('tags', tag));
     (form.files || []).forEach((file) => formData.append('files', file));
      await supportResourcesApi.create(formData);
      setForm({
        title: '',
        description: '',
        audience: 'student',
        subjectId: '',
        grade: '',
        tags: '',
        files: [],
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to upload resources');
    } finally {
      setUploading(false);
    }
  };

  return (
    <EducatorLayout>
      <Box>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={3}>
              <Stack spacing={0.5}>
                <Typography variant="h6" fontWeight={700}>
                  Shared Resources
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload and share reusable resources tagged by grade and subject.
                </Typography>
              </Stack>

              <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Stack spacing={2} component="form" onSubmit={handleSubmit}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Upload new resource
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        label="Title"
                        value={form.title}
                        onChange={(e) => handleFormChange('title', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        select
                        label="Audience"
                        value={form.audience}
                        onChange={(e) => handleFormChange('audience', e.target.value)}
                        sx={{ minWidth: 180 }}
                      >
                        <MenuItem value="student">Students</MenuItem>
                        <MenuItem value="educator">Educators</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                      </TextField>
                    </Stack>
                    <TextField
                      label="Description"
                      value={form.description}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      multiline
                      minRows={2}
                    />
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        select
                        label="Subject"
                        value={form.subjectId}
                        onChange={(e) => handleFormChange('subjectId', e.target.value)}
                        sx={{ minWidth: 240 }}
                      >
                        <MenuItem value="">All subjects</MenuItem>
                        {subjects.map((subject) => (
                          <MenuItem key={subject._id} value={subject._id}>
                            {subject.name} (G{subject.grade})
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Grade"
                        value={form.grade}
                        onChange={(e) => handleFormChange('grade', e.target.value)}
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="">All grades</MenuItem>
                        {gradeOptions.map((grade) => (
                          <MenuItem key={grade} value={grade}>
                            Grade {grade}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Tags (comma separated)"
                        value={form.tags}
                        onChange={(e) => handleFormChange('tags', e.target.value)}
                        fullWidth
                      />
                    </Stack>
                    <TextField
                      label="Files"
                      type="file"
                      inputProps={{ multiple: true }}
                      onChange={handleFiles}
                    />
                    <Button
                      variant="contained"
                      startIcon={<UploadFileRoundedIcon />}
                      type="submit"
                      disabled={uploading}
                    >
 {uploading ? 'Uploading...' : 'Publish resource'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Filter by subject"
                  value={subjectFilter}
                  onChange={(e) => setSubjectFilter(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {subjects.map((subject) => (
                    <MenuItem key={subject._id} value={subject._id}>
                      {subject.name} (G{subject.grade})
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Filter by grade"
                  value={gradeFilter}
                  onChange={(e) => setGradeFilter(e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {gradeOptions.map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      Grade {grade}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Filter by tag"
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {['Maths', 'Science', 'EMS', 'Life Skills'].map((tag) => (
                    <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Grid container spacing={2}>
                {items.map((item) => (
                  <Grid item xs={12} md={6} key={item._id}>
                    <Card elevation={1} sx={{ borderRadius: 3, height: '100%' }}>
                      <CardHeader
                        title={item.title}
                        subheader={item.description || 'No description'}
                        action={
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Chip label={item.audience || 'all'} size="small" />
                            {item.grade && <Chip size="small" label={`G${item.grade}`} />}
                            {item.subjectId && (
                              <Chip
                                size="small"
                                label={
                                  subjects.find((s) => s._id === item.subjectId)?.name || 'Subject'
                                }
                              />
                            )}
                            {(item.tags || []).map((tag) => (
                              <Chip key={tag} label={tag} size="small" />
                            ))}
                          </Stack>
                        }
                      />
                      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                        <Stack spacing={1}>
                          {(item.files || []).map((file) => (
                            <Button
                              key={file.key}
                              startIcon={<DownloadRoundedIcon />}
                              href={file.url}
                              target="_blank"
                              rel="noreferrer"
                              variant="outlined"
                            >
                              {file.originalName}
                            </Button>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {items.length === 0 && !loading && (
                  <Grid item xs={12}>
                    <Alert severity="info">No shared resources yet.</Alert>
                  </Grid>
                )}
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </EducatorLayout>
  );
};

export default EducatorResources;